import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { ContentIdea, DashboardFilters, ExportData } from '@/types/dashboard';
import { logSupabaseError, logError } from '@/utils/sentry';

const REFRESH_INTERVAL = 30000; // 30 seconds

const isJWTExpiredError = (error: { message?: string; code?: string }) =>
  error.message?.includes('JWT expired') || error.code === 'PGRST301';

export const useDashboard = (userId: string | null) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [contentIdeas, setContentIdeas] = useState<ContentIdea[]>([]);
  const [filteredIdeas, setFilteredIdeas] = useState<ContentIdea[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [newContentCount, setNewContentCount] = useState(0);
  const [filters, setFilters] = useState<DashboardFilters>({
    all: true,
    highViralScore: false,
    recent24h: false
  });
  
  const { toast } = useToast();

  /**
   * Fetch content ideas from database
   */
  const fetchContentIdeas = useCallback(async (isRefresh = false, _isRetry = false) => {
    if (!userId) {
      setIsLoading(false);
      return;
    }

    if (isRefresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    setError(null);

    try {
      // Fetch user-generated content FIRST (always, regardless of inspiration sources)
      const { data: userGeneratedData, error: userGeneratedError } = await supabase
        .from('user_generated_content')
        .select('*')
        .eq('user_id', userId)
        .or('is_trashed.is.null,is_trashed.eq.false')
        .order('created_at', { ascending: false });

      if (userGeneratedError) {
        // JWT expired — refresh session and retry once
        if (!_isRetry && isJWTExpiredError(userGeneratedError)) {
          await supabase.auth.refreshSession();
          return fetchContentIdeas(isRefresh, true);
        }
        // Skip Sentry for transient network errors — dashboard auto-retries every 30s
        const isNetworkError = userGeneratedError.message?.includes('Failed to fetch')
          || userGeneratedError.message?.includes('NetworkError')
          || userGeneratedError.message?.includes('Load failed');
        if (!isNetworkError) {
          logSupabaseError('Dashboard: fetch user-generated content', userGeneratedError);
        }
      }

      // Transform user-generated content with explicit user_id filter (defense-in-depth)
      const generatedIdeas: ContentIdea[] = (userGeneratedData || [])
        .filter((item) => item.user_id === userId)
        .map((item) => ({
          id: item.id,
          user_id: item.user_id,
          source_scraped_content_id: null,
          title: item.title,
          description: item.description,
          content: item.content,
          edited_content: item.edited_content,
          platform: item.platform,
          viral_score: null, // user_generated_content doesn't have viral_score
          hashtags: null, // user_generated_content doesn't have hashtags
          created_at: item.created_at,
          updated_at: item.updated_at,
          is_favorited: item.is_favorited || false,
          favorited_at: item.favorited_at,
          is_used: item.is_used || false,
          used_at: item.used_at,
          alt_hooks: item.alt_hooks,
          text_overlays: item.text_overlays,
          improvements_applied: item.improvements_applied,
          perplexity_insights: item.perplexity_insights,
          source_competitor: null,
          source: 'user_generated' as const,
          main_idea: item.main_idea
        }));

      // Get user's inspiration sources
      const { data: inspirationSources, error: sourcesError } = await supabase
        .from('inspiration_sources')
        .select('username')
        .eq('user_id', userId);

      if (sourcesError) {
        if (!_isRetry && isJWTExpiredError(sourcesError)) {
          await supabase.auth.refreshSession();
          return fetchContentIdeas(isRefresh, true);
        }
        const isNetworkError = sourcesError.message?.includes('Failed to fetch')
          || sourcesError.message?.includes('NetworkError')
          || sourcesError.message?.includes('Load failed');
        if (!isNetworkError) {
          logSupabaseError('Dashboard: fetch inspiration sources', sourcesError);
        }
        // Still return generated ideas even if sources fail
        setContentIdeas(generatedIdeas);
        setLastRefresh(new Date());
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      const userSourceUsernames = (inspirationSources || []).map(source => source.username);

      // If no inspiration sources, return only generated content
      if (userSourceUsernames.length === 0) {
        const ideas = generatedIdeas.sort((a, b) => 
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        setContentIdeas(ideas);
        setLastRefresh(new Date());
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      // Get content ideas that are linked to scraped content from user's inspiration sources
      // Filter to last 14 days for top ideas display
      const fourteenDaysAgo = new Date();
      fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
      
      const { data, error: fetchError } = await supabase
        .from('content_ideas')
        .select(`
          *,
          source_competitor:scraped_content!content_ideas_source_scraped_content_id_fkey(username, likes_count, comments_count, engagement_rate, url, video_url)
        `)
        .eq('user_id', userId)
        .not('source_scraped_content_id', 'is', null)
        .or('is_trashed.is.null,is_trashed.eq.false')
        .gte('created_at', fourteenDaysAgo.toISOString())
        .order('viral_score', { ascending: false, nullsFirst: false })
        .order('created_at', { ascending: false });

      if (fetchError) {
        if (!_isRetry && isJWTExpiredError(fetchError)) {
          await supabase.auth.refreshSession();
          return fetchContentIdeas(isRefresh, true);
        }
        const isNetworkError = fetchError.message?.includes('Failed to fetch')
          || fetchError.message?.includes('NetworkError')
          || fetchError.message?.includes('Load failed');
        if (!isNetworkError) {
          logSupabaseError('Dashboard: fetch content ideas', fetchError);
        }
        setError('Failed to load content ideas');
        setIsLoading(false);
        setIsRefreshing(false);
        return;
      }

      // Use all content ideas (don't filter by source username)
      const filteredData = (data || []);

      // Transform scraped content ideas with explicit user_id filter (defense-in-depth)
      const scrapedIdeas: ContentIdea[] = filteredData
        .filter((item) => item.user_id === userId)
        .map((item) => ({
          id: item.id,
          user_id: item.user_id,
          source_scraped_content_id: item.source_scraped_content_id,
          title: item.title,
          description: item.description,
          content: item.content,
          edited_content: item.edited_content,
          platform: item.platform,
          viral_score: item.viral_score,
          hashtags: item.hashtags,
          created_at: item.created_at,
          updated_at: item.updated_at,
          is_favorited: item.is_favorited || false,
          favorited_at: item.favorited_at,
          is_used: item.is_used || false,
          used_at: item.used_at,
          alt_hooks: item.alt_hooks,
          text_overlays: item.text_overlays,
          improvements_applied: item.improvements_applied,
          perplexity_insights: item.perplexity_insights,
          source_competitor: item.source_competitor ? {
            username: item.source_competitor.username,
            likes_count: item.source_competitor.likes_count,
            comments_count: item.source_competitor.comments_count,
            engagement_rate: item.source_competitor.engagement_rate,
            url: item.source_competitor.url,
            video_url: item.source_competitor.video_url
          } : null,
          source: 'scraped' as const
        }));

      // Combine and sort by created_at (newest first)
      const ideas = [...scrapedIdeas, ...generatedIdeas].sort((a, b) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );

      // Check for new content during refresh
      if (isRefresh) {
        setContentIdeas((prevIdeas) => {
          const newIdeas = ideas.filter(idea => 
            !prevIdeas.some(existing => existing.id === idea.id)
          );
          
          if (newIdeas.length > 0) {
            setNewContentCount(newIdeas.length);
            toast({
              title: "Nowe pomysły na wideo są gotowe",
              description: `Wygenerowano ${newIdeas.length} ${newIdeas.length === 1 ? 'nowy pomysł' : newIdeas.length < 5 ? 'nowe pomysły' : 'nowych pomysłów'}`,
            });
          }
          
          return ideas;
        });
      } else {
        setContentIdeas(ideas);
      }

      setLastRefresh(new Date());
      
    } catch (error) {
      logError('Dashboard: unexpected fetch error', error, { hook: 'useDashboard' });
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, [userId, toast]);

  /**
   * Apply filters to content ideas
   */
  const applyFilters = useCallback(() => {
    let filtered = [...contentIdeas];

    if (filters.highViralScore && !filters.all) {
      filtered = filtered.filter(idea => (idea.viral_score || 0) >= 70);
    }

    if (filters.recent24h && !filters.all) {
      const oneDayAgo = new Date();
      oneDayAgo.setDate(oneDayAgo.getDate() - 1);
      filtered = filtered.filter(idea => 
        new Date(idea.created_at) > oneDayAgo
      );
    }

    setFilteredIdeas(filtered);
  }, [contentIdeas, filters]);

  /**
   * Update filters
   */
  const updateFilters = useCallback((newFilters: Partial<DashboardFilters>) => {
    setFilters(prev => {
      // If selecting a specific filter, uncheck "all"
      if (newFilters.highViralScore || newFilters.recent24h) {
        return { ...prev, ...newFilters, all: false };
      }
      
      // If selecting "all", uncheck specific filters
      if (newFilters.all) {
        return { all: true, highViralScore: false, recent24h: false };
      }
      
      return { ...prev, ...newFilters };
    });
    setNewContentCount(0); // Clear notification when user interacts
  }, []);

  /**
   * Copy content to clipboard
   */
  const copyToClipboard = useCallback(async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast({
        title: "Skopiowano!",
      });
    } catch (error) {
      console.error('Copy failed:', error);
      toast({
        title: "Copy failed",
        description: "Unable to copy to clipboard",
        variant: "destructive"
      });
    }
  }, [toast]);

  /**
   * Copy all scripts
   */
  const copyAllScripts = useCallback(() => {
    const allScripts = filteredIdeas
      .map(idea => `# ${idea.title}\n\n${idea.content || 'No content available'}\n\n---\n`)
      .join('\n');
    
    copyToClipboard(allScripts, 'All scripts');
  }, [filteredIdeas, copyToClipboard]);

  /**
   * Export to CSV
   */
  const exportToCSV = useCallback(() => {
    const csvData: ExportData[] = filteredIdeas.map(idea => ({
      title: idea.title,
      description: idea.description || '',
      content: idea.content || '',
      viral_score: idea.viral_score || 0,
      platform: idea.platform || '',
      created_at: idea.created_at
    }));

    const headers = ['Title', 'Description', 'Content', 'Viral Score', 'Platform', 'Created At'];
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => [
        `"${row.title.replace(/"/g, '""')}"`,
        `"${row.description.replace(/"/g, '""')}"`,
        `"${row.content.replace(/"/g, '""')}"`,
        row.viral_score,
        row.platform,
        row.created_at
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `content-ideas-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast({
      title: "Exported!",
      description: `${filteredIdeas.length} content ideas exported to CSV`,
    });
  }, [filteredIdeas, toast]);

  /**
   * Manual refresh
   */
  const refresh = useCallback(() => {
    fetchContentIdeas(true);
  }, [fetchContentIdeas]);

  // Proactively refresh session when tab regains focus (prevents JWT expired errors)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && userId) {
        supabase.auth.refreshSession();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [userId]);

  // Initial fetch
  useEffect(() => {
    if (userId) {
      fetchContentIdeas(false);
    }
  }, [userId, fetchContentIdeas]);

  // Auto-refresh interval
  useEffect(() => {
    if (!userId) return;

    const interval = setInterval(() => {
      fetchContentIdeas(true);
    }, REFRESH_INTERVAL);

    return () => clearInterval(interval);
  }, [userId, fetchContentIdeas]);

  // Apply filters when content or filters change
  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  return {
    isLoading,
    isRefreshing,
    contentIdeas,
    filteredIdeas,
    error,
    lastRefresh,
    newContentCount,
    filters,
    updateFilters,
    copyToClipboard,
    copyAllScripts,
    exportToCSV,
    refresh
  };
};